import React, { useState, useCallback, useEffect } from 'react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuLabel
} from '@core/components/ui/dropdown-menu';
import { FilledButton, Input, DropdownInput } from '../../components';
import { NotepadTextDashed, Plus, Edit, Trash } from 'lucide-react';
import { debounce } from 'lodash';

const TemplateMenu = ({
  editor,
  templates,
  templateCategories,
  onTemplateSelect,
  onCreateTemplate,
  onDeleteTemplate,
  onUpdateTemplate,
  defaultSelectedCategory
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(
    defaultSelectedCategory || 'all'
  );
  const [fetchedTemplates, setFetchedTemplates] = useState([]);
  const [fetchedCategories, setFetchedCategories] = useState([]);
  const [isSavingMode, setIsSavingMode] = useState(false);
  const [newTemplateTitle, setNewTemplateTitle] = useState('');
  const [newTemplateCategory, setNewTemplateCategory] = useState('all');
  const [editingTemplate, setEditingTemplate] = useState(null);

  const debouncedQueryChange = useCallback(
    debounce(async (value, category) => {
      const results = await templates(value, category);
      setFetchedTemplates(results);
    }, 300),
    [templates]
  );

  const fetchInitialTemplates = useCallback(async () => {
    const results = await templates('', selectedCategory);
    setFetchedTemplates(results);
  }, [templates, selectedCategory]);

  const fetchTemplateCategories = useCallback(async () => {
    const results = await templateCategories();
    setFetchedCategories(results);
  }, [templateCategories]);

  useEffect(() => {
    fetchInitialTemplates();
    fetchTemplateCategories();
  }, [fetchInitialTemplates, fetchTemplateCategories]);

  // Effect to synchronize selectedCategory state with defaultSelectedCategory prop changes
  useEffect(() => {
    const newCategoryFromProp = defaultSelectedCategory || 'all';
    if (selectedCategory !== newCategoryFromProp) {
      setSelectedCategory(newCategoryFromProp);
      // Re-fetch templates when the default category changes due to the prop
      debouncedQueryChange(inputValue, newCategoryFromProp);
    }
  }, [defaultSelectedCategory, inputValue, debouncedQueryChange]); // Removed selectedCategory from deps

  const handleInputChange = useCallback(
    event => {
      const value = event.target.value;
      setInputValue(value);
      debouncedQueryChange(value, selectedCategory);
    },
    [debouncedQueryChange, selectedCategory]
  );

  const handleCategoryChange = useCallback(
    event => {
      const category = event.target.value;
      setSelectedCategory(category);
      debouncedQueryChange(inputValue, category);
    },
    [debouncedQueryChange, inputValue]
  );

  const handleSelect = useCallback(
    (event, selectedTemplate) => {
      event.preventDefault();
      event.stopPropagation();

      if (onTemplateSelect) {
        onTemplateSelect(selectedTemplate);
      }

      setInputValue('');
      setFetchedTemplates([]);
      setIsOpen(false);
    },
    [onTemplateSelect]
  );

  const handleOpenChange = useCallback(
    open => {
      setIsOpen(open);
      if (open) {
        fetchInitialTemplates();
        if (editingTemplate) {
          setIsSavingMode(true);
        }
      } else {
        setInputValue('');
        setFetchedTemplates([]);
        // Reset saving mode if dropdown is closed without saving/cancelling
        if (isSavingMode) {
          handleCancelSave();
        }
      }
    },
    [fetchInitialTemplates, editingTemplate, isSavingMode] // Added isSavingMode and handleCancelSave
  );

  const handleCreateButtonClick = () => {
    setIsSavingMode(true);
    if (fetchedCategories.length > 0) {
      setNewTemplateCategory(fetchedCategories[0].id);
    } else {
      setNewTemplateCategory('all');
    }
  };

  const handleSaveTemplate = async () => {
    if (editor?.isEmpty) return; // Should be caught by disabled state, but as a safeguard

    if (editingTemplate) {
      await onUpdateTemplate({
        ...editingTemplate,
        title: newTemplateTitle,
        content: editor.getHTML(),
        category: newTemplateCategory
      });
    } else if (onCreateTemplate) {
      await onCreateTemplate({
        title: newTemplateTitle,
        content: editor.getHTML(),
        category: newTemplateCategory
      });
    }
    fetchInitialTemplates();
    setIsSavingMode(false);
    setNewTemplateTitle('');
    setNewTemplateCategory('all');
    setEditingTemplate(null);
  };

  const handleCancelSave = () => {
    setIsSavingMode(false);
    setNewTemplateTitle('');
    setNewTemplateCategory('all');
    setEditingTemplate(null);
  };

  const handleEditTemplate = template => {
    setEditingTemplate(template);
    setNewTemplateTitle(template.title);
    if (!template.category && fetchedCategories.length > 0) {
      setNewTemplateCategory(fetchedCategories[0].id);
    } else {
      setNewTemplateCategory(template.category || 'all');
    }
    editor.commands.setContent(template.content);
    setIsSavingMode(true);
  };

  const handleDeleteTemplate = template => {
    if (onDeleteTemplate) {
      onDeleteTemplate(template.id);
      // After deleting, refresh the template list
      fetchInitialTemplates();
    }
  };

  if (!onTemplateSelect) {
    return null;
  }

  const isEditorContentEmpty = editor?.isEmpty;

  return (
    <>
      <DropdownMenu open={isOpen} onOpenChange={handleOpenChange}>
        <DropdownMenuTrigger asChild>
          <button
            className={`p-2 rounded hover:bg-base-200 ${
              editingTemplate ? 'bg-blue-100' : ''
            }`}
            title={
              editingTemplate
                ? `Editing: ${editingTemplate.title}`
                : 'Select a Template'
            }
          >
            <NotepadTextDashed size={18} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className='w-80'
          onCloseAutoFocus={e => e.preventDefault()}
        >
          <div className='p-2' onClick={e => e.stopPropagation()}>
            {isSavingMode ? (
              <div className='flex flex-col space-y-2'>
                <Input
                  label='Title'
                  value={newTemplateTitle}
                  onChange={e => setNewTemplateTitle(e.target.value)}
                  placeholder='Enter Template Title...'
                  required
                />
                <DropdownInput
                  value={newTemplateCategory}
                  onChange={e => setNewTemplateCategory(e.target.value)}
                  options={fetchedCategories.map(category => ({
                    value: category.id,
                    label: category.type
                  }))}
                  disablePlaceholder
                  className='text-sm'
                />
                {isSavingMode && !editingTemplate && isEditorContentEmpty && (
                  <p className='text-xs text-gray-500 mt-1'>
                    Type your template content in the editor, then save it here.
                  </p>
                )}
                <div className='flex space-x-2'>
                  <FilledButton
                    colour='primary'
                    size='sm'
                    onClick={handleSaveTemplate}
                    disabled={
                      !newTemplateTitle ||
                      (fetchedCategories.length > 0 &&
                        newTemplateCategory === 'all') ||
                      isEditorContentEmpty
                    }
                  >
                    Save
                  </FilledButton>
                  <FilledButton
                    colour='base'
                    size='sm'
                    onClick={handleCancelSave}
                  >
                    Cancel
                  </FilledButton>
                </div>
              </div>
            ) : (
              <>
                <div className='flex space-x-1'>
                  <Input
                    value={inputValue}
                    onChange={handleInputChange}
                    placeholder='Search Templates...'
                    rightAddon={
                      <select
                        value={selectedCategory}
                        onChange={handleCategoryChange}
                        className='border-0 bg-transparent text-sm text-gray-900 focus:ring-0 p-0 m-0 w-20 truncate'
                      >
                        <option value='all'>All</option>
                        {fetchedCategories.length > 0 &&
                          fetchedCategories.map(category => (
                            <option key={category.id} value={category.id}>
                              {category.type}
                            </option>
                          ))}
                      </select>
                    }
                  />

                  {onCreateTemplate && (
                    <FilledButton
                      colour='base'
                      size='sm'
                      onClick={handleCreateButtonClick}
                      title='Create new template from editor content'
                    >
                      <Plus />
                    </FilledButton>
                  )}
                </div>
                {fetchedTemplates.length > 0 ? (
                  <DropdownMenuGroup className='p-0 mt-2 max-h-60 overflow-y-auto'>
                    <DropdownMenuLabel>Templates</DropdownMenuLabel>
                    {fetchedTemplates.map(template => (
                      <div
                        key={template.id}
                        className='cursor-pointer select-none hover:bg-base-100 p-2 rounded flex justify-between items-center'
                      >
                        <span
                          className='block truncate font-normal text-sm flex-grow'
                          onClick={e => handleSelect(e, template)}
                        >
                          {template.title}
                        </span>
                        <div className='flex space-x-2 ml-2 flex-shrink-0'>
                          {onUpdateTemplate && (
                            <button
                              className='text-blue-500 hover:text-blue-700'
                              onClick={() => handleEditTemplate(template)}
                              title='Edit template'
                            >
                              <Edit size={16} />
                            </button>
                          )}
                          {onDeleteTemplate && (
                            <button
                              className='text-red-500 hover:text-red-700'
                              onClick={() => handleDeleteTemplate(template)}
                              title='Delete template'
                            >
                              <Trash size={16} />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </DropdownMenuGroup>
                ) : (
                  <div className='text-gray-500 text-sm mt-2'>
                    No templates found.
                  </div>
                )}
              </>
            )}
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
};

export default React.memo(TemplateMenu);
